import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PortraitAspectRatioCalculator = () => {
  const [inputData, setInputData] = useState({
    width: '',
    height: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateAspectRatio = () => {
    const { width, height } = inputData;
    
    // Validate input
    if (!width || !height) {
      setError('Please enter both width and height');
      return;
    }

    // Validate numeric input
    if (isNaN(width) || isNaN(height)) {
      setError('Please enter valid numbers for width and height');
      return;
    }

    // Convert to numbers
    const numericWidth = parseInt(width);
    const numericHeight = parseInt(height);

    // Ensure portrait orientation
    if (numericWidth > numericHeight) {
      setError('For portrait aspect ratio, height should be greater than width');
      return;
    }

    // Calculate greatest common divisor
    const gcd = (a, b) => {
      return b === 0 ? a : gcd(b, a % b);
    };

    const greatestCommonDivisor = gcd(numericWidth, numericHeight);

    // Simplify the ratio
    const simplifiedWidth = numericWidth / greatestCommonDivisor;
    const simplifiedHeight = numericHeight / greatestCommonDivisor;

    const result = `${simplifiedWidth}:${simplifiedHeight}`;

    // Create visualization data
    const values = [numericWidth, numericHeight];
    const labels = ['Width', 'Height'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Dimensions',
        data: values,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Find the greatest common divisor (GCD) of width and height',
      `GCD of ${numericWidth} and ${numericHeight} is ${greatestCommonDivisor}`,
      '\nStep 2: Divide both dimensions by the GCD',
      `Width: ${numericWidth} / ${greatestCommonDivisor} = ${simplifiedWidth}`,
      `Height: ${numericHeight} / ${greatestCommonDivisor} = ${simplifiedHeight}`,
      '\nStep 3: Format as a ratio',
      `Result: ${result}`
    ];

    setResults({
      width: numericWidth,
      height: numericHeight,
      result,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    width: '750',
    height: '1334',
    result: '750:1334' // This would simplify to 375:667, but let's keep it as is for the example
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Portrait Aspect Ratio Calculator | Image and Screen Tool</title>
          <meta name="description" content="Calculate the portrait aspect ratio for images and screens. This calculator helps determine the simplified ratio based on width and height dimensions." />
          <meta name="keywords" content="portrait aspect ratio calculator, image aspect ratio, screen ratio calculator, photography tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🖼️</div>
            <h1 className="text-3xl font-bold text-gray-800">Portrait Aspect Ratio Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the portrait aspect ratio for images and screens. This calculator helps determine the simplified ratio based on width and height dimensions, ensuring the height is greater than the width for proper portrait orientation.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (pixels):</label>
              <input
                type="text"
                name="width"
                value={inputData.width}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter width in pixels (e.g., 750)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (pixels):</label>
              <input
                type="text"
                name="height"
                value={inputData.height}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter height in pixels (e.g., 1334)"
              />
            </div>
          </div>

          <button
            onClick={calculateAspectRatio}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Aspect Ratio
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Aspect Ratio Calculation</h3>
                  <p className="text-lg">Width: {results.width} pixels</p>
                  <p className="text-lg">Height: {results.height} pixels</p>
                  <p className="text-lg font-bold mt-4">Portrait Aspect Ratio: {results.result}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Simplified using greatest common divisor</p>
                  <p className="text-lg">Orientation: Portrait (height greater than width)</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Pixels'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Dimensions'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Portrait Dimensions'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a common smartphone resolution:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Width: {exampleData.width} pixels</li>
                <li>Height: {exampleData.height} pixels</li>
                <li>Portrait Aspect Ratio: {exampleData.result}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Find the greatest common divisor of 750 and 1334
                  <ul className="list-disc pl-6">
                    <li>GCD of 750 and 1334 is 2</li>
                  </ul>
                </li>
                <li>Divide both dimensions by the GCD
                  <ul className="list-disc pl-6">
                    <li>750 / 2 = 375</li>
                    <li>1334 / 2 = 667</li>
                  </ul>
                </li>
                <li>Format as a ratio
                  <ul className="list-disc pl-6">
                    <li>375:667</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Portrait Aspect Ratio Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Dimensions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the width and height in pixels</li>
                  <li>Ensure height is greater than width for portrait orientation</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The simplified aspect ratio</li>
                  <li>The calculation details</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated aspect ratio</li>
                  <li>Check the calculation steps</li>
                  <li>View the visual representation of dimensions</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Common portrait aspect ratios include 3:4, 9:16, and 1:2</li>
                <li>Use this calculator for photos, videos, and screen dimensions</li>
                <li>Aspect ratio helps maintain proportions when resizing images</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Portrait Aspect Ratio <span className="text-blue-600">🖼️</span></h2>
          <div className="text-gray-600">
            <p>
              Portrait aspect ratio describes the proportional relationship between the width and height of an image or screen in vertical orientation. It's important because:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Visual Presentation:</strong> Ensures content appears correctly oriented</li>
              <li><strong>Design Consistency:</strong> Maintains proportional layout in applications</li>
              <li><strong>Printing:</strong> Helps avoid unwanted cropping or stretching</li>
              <li><strong>Responsive Design:</strong> Critical for websites and applications that adapt to different devices</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your dimension values</li>
              <li><strong>Orientation:</strong> Always ensure height greater than width for portrait calculations</li>
              <li><strong>Common Ratios:</strong> Familiarize yourself with standard portrait ratios for different applications</li>
              <li><strong>Documentation:</strong> Record aspect ratios for design consistency</li>
            </ul>
            <p className="italic mt-4">
              Note: Aspect ratio is a dimensionless value that describes the proportional relationship between width and height.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PortraitAspectRatioCalculator;